import { createSelector } from 'reselect'

import { selectDefaultTicketSearch } from 'selectors/search/default'
import { selectTicketSearchOperatorValueMap } from 'selectors/search/base'
import { constructEncodedSearchQueryString } from 'util/search'
import { emptyObj } from 'util/objects'

const base = state => state.location || emptyObj

export const selectCurrentPath = state => base(state).pathname

export const selectCurrentPage = state => base(state).type

const selectCurrentSearch = state => base(state).search

export const selectCurrentPayload = state => base(state).payload || emptyObj

export const selectQueryParams = state => base(state).query || emptyObj

export const selectPrevious = state => base(state).prev

const selectPreviousSearch = state => (base(state).prev || emptyObj).search

export const selectCurrentTicketAction = createSelector(
  selectCurrentPage,
  selectCurrentPayload,
  selectQueryParams,
  (type, payload, query) => {
    return {
      type,
      payload,
      meta: { query },
    }
  }
)

export const selectCurrentPageWithCollapsedHeaderAction = createSelector(
  selectCurrentTicketAction,
  currentTicketAction => {
    const update = {
      meta: {
        ...currentTicketAction.meta,
        query: { ...currentTicketAction.meta.query, expand: false },
      },
    }
    return { ...currentTicketAction, ...update }
  }
)

export const selectCurrentId = createSelector(
  selectCurrentPayload,
  payload => payload.id
)

export const selectCurrentTicketIdFromPage = createSelector(
  selectCurrentPath,
  selectCurrentId,
  (path, id) => {
    if (!path.match('/tickets/')) return undefined
    if (path.match('/tickets/new')) return 'new'
    return id
  }
)

export const selectCurrentTicketId = createSelector(
  selectCurrentPath,
  selectQueryParams,
  selectCurrentTicketIdFromPage,
  (path, params, idFromPage) => {
    if (!path) return null
    if (idFromPage) return idFromPage
    if (path.match('/tickets/')) {
      if (path.match(/\/tickets\/new/)) return 'new'
      if (path.match(/\/tickets\/log-conversation/)) return 'new'
      return path.match(/\/tickets\/(\w*)/)[1]
    }
    if (params && params.conversationId) return params.conversationId
    return null
  }
)

function fullPath(path, search) {
  if (!search) return path
  return `${path}?${search}`
}

export const selectCurrentPathWithSearch = createSelector(
  selectCurrentPath,
  selectCurrentSearch,
  fullPath
)

// HACK (jscheel): This is ghetto, but we have some paths that are partially
// encoded, and we need to check against them to make sure the right leftnav
// item is highlighted on page load. There appears to be a problem with the way
// spaces are stored and retrieved, so we just force what we need.
export const selectCurrentPathWithSearchEncoded = createSelector(
  selectCurrentPath,
  selectDefaultTicketSearch,
  selectTicketSearchOperatorValueMap,
  (path, defaultSearch, idValueMap) => {
    if (path === '/')
      return `/search/${constructEncodedSearchQueryString(
        defaultSearch,
        idValueMap
      )}`

    const trimmedPath = path.replace(/^\/search\//, '')
    return `/search/${encodeURIComponent(decodeURIComponent(trimmedPath))}`
  }
)
// END HACK

export const selectPreviousPath = createSelector(
  selectPrevious,
  prev => prev.pathname
)

export const selectPreviousPathWithSearch = createSelector(
  selectPreviousPath,
  selectPreviousSearch,
  fullPath
)

// Jank workaround for https://github.com/faceyspacey/redux-first-router/issues/183
// TLDR; we have to live with double dispatches, but we can avoid the big
// perf hit caused by fetching tickets twice, if we just check for the
// double dispatch.
//
// What happens is we see the first dispatch with a current path like
//   "/search/inbox%3A0927513294 folder%3A3114099215"
//
// and the same previous path, except with more encoding!
//   "/search/inbox%3A0927513294%20folder%3A3114099215"
//
// Then we see a second dispatch (with the same current path, but the 'real'
// previous path.)
//
export const selectIsRFRBeingStupid = createSelector(
  selectCurrentPathWithSearch,
  selectPreviousPathWithSearch,
  (current, prev) => prev === current.replace(/\s/, '%20')
)

export function isInboxPath(path) {
  if (path === '/') return true
  if (path.match(/^\/search/)) return true
  if (path.match(/^\/tickets/)) return true
  return false
}

export function isChatPath(path) {
  if (path.match(/^\/chats/)) return true
  return false
}

export function isOnboardingPath(path) {
  if (path.match(/^\/setup/)) return true
  if (path.match(/^\/signup/)) return true
  return false
}

export const selectIsInInbox = createSelector(selectCurrentPath, isInboxPath)

export const selectIsInChat = createSelector(selectCurrentPath, isChatPath)

export const selectIsInOnboarding = createSelector(
  selectCurrentPath,
  isOnboardingPath
)

export const selectIsOnSearchPage = createSelector(selectCurrentPath, path => {
  if (path === '/') return true
  if (path.match(/^\/search/)) return true
  return false
})

export const selectIsOnSearchPageWithoutFirstTicketLoaded = createSelector(
  selectIsOnSearchPage,
  selectQueryParams,
  (isOnSearchPage, params) => {
    if (isOnSearchPage && !params.conversationId) return true
    return false
  }
)

// Don't really need state for selectSubdomain, selectHost and selectIsAccountSubdomain
// but im adding it for future proofing. We should probably create reducer which adds
// this information into the state and selects it from there.
export function selectHost() {
  return window.location.host
}

export const selectSubdomain = createSelector(
  selectHost,
  host => host.split('.')[0]
)

export const selectIsAccountSubdomain = createSelector(
  selectHost,
  selectSubdomain,
  (host, subdomain) =>
    !host.match(/\.alpha\./) &&
    !subdomain.match(/^(app|mobile|mobile-staging|beta|www|mobile-frontend)$/)
)

export const selectAccountSubdomainFromLocation = createSelector(
  selectIsAccountSubdomain,
  selectSubdomain,
  (isAccountSubdomain, subdomain) => {
    if (isAccountSubdomain) {
      return subdomain
    }
    return null
  }
)

export const selectIsInKb = createSelector(selectCurrentPath, path => {
  if (path.match(/^\/kb/)) return true
  return false
})

export function selectLocationKind(state) {
  return state.location.kind
}
